import React from 'react';
import { Link } from 'react-router-dom';

const triggerAnimations = (): void => {
  console.log("Triggering forceAnimations event from Footer");
  window.dispatchEvent(new CustomEvent('forceAnimations', {
    detail: { timestamp: Date.now() }
  }));
};

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const navColumns = [
    {
      title: 'Company',
      links: [
        { name: 'About', href: '/#about' },
        { name: 'Approach', href: '/#approach' },
        { name: 'Our Process', href: '/#team' },
        { name: 'Get Started', href: '/get-started' },
      ]
    },
    {
      title: 'Resources',
      links: [
        { name: 'Platforms', href: '/#platforms' },
        { name: 'Acquisition Paths', href: '/#specialty-structures' },
        { name: 'Focus Areas', href: '/#portfolio' },
        { name: 'Contact', href: '/#contact' },
      ]
    }
  ];

  const legalLinks = [
    { label: 'Privacy Policy', href: '/privacy-policy' },
    { label: 'Terms of Service', href: '/terms-of-service' },
    { label: 'Cookie Policy', href: '/cookie-policy' },
    { label: 'Disclaimer', href: '/disclaimer' },
  ];

  return (
    <footer className="relative bg-transparent text-gray-700 mt-20 pt-32">
      <div className="absolute inset-x-0 top-0 h-96 bg-gradient-to-b from-white via-white to-blue-50/80 z-0"></div>
      <div className="absolute inset-0 top-64 bg-gradient-to-b from-blue-50 to-blue-100 z-0"></div>
      <div className="absolute inset-0 z-0 overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGZpbGw9IiNmZmZmZmYiIGQ9Ik0wIDBoNjB2NjBIMHoiLz48cGF0aCBkPSJNNjAgMEgwdjYwaDYwVjB6TTMwIDMwaDMwVjBoLTMwdjMwek0wIDMwaDMwVjBoLTMwdjMwek0wIDYwaDMwVjMwaC0zMHYzMHpNMzAgNjBoMzBWMzBoLTMwdjMweiIgc3Ryb2tlPSIjZTBlNWViIiBzdHJva2Utb3BhY2l0eT0iLjUiLz48L2c+PC9zdmc+')] opacity-30"></div>
        <div className="absolute inset-x-0 top-0 h-96 bg-gradient-to-b from-white via-white/95 to-transparent z-10"></div>
      </div>

      <div className="container relative z-20 mx-auto px-4 md:px-6 pt-16 pb-8">
        <div className="flex flex-col md:flex-row justify-between mb-16">
          <div className="md:w-1/3 mb-8 md:mb-0">
            <Link to="/" className="flex items-center mb-6">
              <img
                src="https://www.acsecapital.com/acse-capital-inc-email.png"
                alt="ACSE Capital Logo"
                className="h-16 object-contain"
              />
            </Link>
            <p className="text-gray-600 mb-6 max-w-md">
              ACSE Capital focuses on acquiring, building, and supporting AI-related SaaS startups, software platforms, and online businesses.
            </p>
          </div>

          <div className="md:w-2/3 flex flex-wrap">
            {navColumns.map((column, idx) => (
              <div key={idx} className="w-1/2 md:w-1/3 mb-6 md:mb-0">
                <h4 className="text-gray-800 font-semibold mb-4">{column.title}</h4>
                <ul className="space-y-2">
                  {column.links.map((link, linkIdx) => (
                    <li key={linkIdx}>
                      {link.href.startsWith('/get-started') ? (
                        <Link
                          to="/get-started"
                          className={`hover:text-primary-600 transition-colors ${
                            link.name === 'Get Started' ? 'text-primary-600 font-medium' : 'text-gray-600'
                          }`}
                        >
                          {link.name}
                        </Link>
                      ) : (
                        <a
                          href={link.href}
                          className="text-gray-600 hover:text-primary-600 transition-colors"
                          onClick={(e) => {
                            if (link.href.includes('#')) {
                              e.preventDefault();
                              const id = link.href.split('#')[1];

                              if (window.location.pathname !== '/') {
                                console.log(`Footer nav: Not on homepage, navigating to /#${id}`);
                                sessionStorage.setItem('scrollToSection', id);
                                window.location.href = '/';
                                return;
                              } else {
                                const element = document.getElementById(id);
                                if (element) {
                                  console.log(`Footer nav: Found element #${id}, scrolling into view`);
                                  element.scrollIntoView({ behavior: 'smooth' });
                                  triggerAnimations();
                                } else {
                                  console.error(`Footer nav: Element #${id} not found`);
                                }
                              }
                            }
                          }}
                        >
                          {link.name}
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="pt-8 border-t border-gray-200/30 flex flex-col md:flex-row justify-between items-center">
          <div className="text-gray-600 mb-4 md:mb-0">
            © {currentYear} ACSE Capital Inc. All rights reserved.
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            {legalLinks.map((link, index) => (
              <Link
                key={index}
                to={link.href}
                className="text-gray-600 hover:text-primary-600 transition-colors"
                onClick={() => window.scrollTo(0, 0)}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
